import { lazy, Suspense } from 'react'

import type { InitiativeRegistry } from './runtime'

import { DESIGN_QA_SCENARIOS_ENABLED, designQaState as state, mayHaveDesignQaScenario } from './page-state'

export {
  areDesignQaOverlaysHidden,
  DESIGN_QA_SCENARIOS_ENABLED,
  getDesignQaVariant,
  setDesignQaScenarioFlags
} from './page-state'

export type {
  Initiative,
  InitiativeRegistry,
  InitiativeScenarios,
  Scenario,
  ScenarioHandler,
  ScenarioHandlers,
  ScenarioRequest,
  ScenarioResponse,
  Variant,
  ViewportSize,
  Viewports
} from './runtime'

// Everything the apps may import up front. The runtime (`/runtime`) and the dock load lazily behind the gate.

/** `fn`'s promise, cached until it rejects. */
function once<T>(fn: () => Promise<T>): () => Promise<T> {
  let promise: Promise<T> | undefined
  return () =>
    (promise ??= fn().catch((error) => {
      promise = undefined
      throw error
    }))
}

/** `registry` with each initiative loaded at most once. */
function loadingOnce(registry: InitiativeRegistry): InitiativeRegistry {
  return Object.fromEntries(Object.entries(registry).map(([slug, loader]) => [slug, once(loader)]))
}

/**
 * Hands the registry to the dock and installs the `?scenario=` in effect. Never throws, so a broken scenario can't block
 * boot. A no-op when scenarios are off. `settings` is read per request, so it may still be loading.
 */
export async function startDesignQaScenarios(load: () => Promise<InitiativeRegistry>, settings: object): Promise<void> {
  if (!DESIGN_QA_SCENARIOS_ENABLED) return
  const registry = once(async () => loadingOnce(await load()))
  state.loadInitiatives = registry
  if (!mayHaveDesignQaScenario(window.location.search)) return
  try {
    const [{ installDesignQaScenarios, apiOriginsFrom }, initiatives] = await Promise.all([
      import('./runtime'),
      registry()
    ])
    // Fixtures answer only the app's own APIs
    await installDesignQaScenarios(initiatives, { apiOrigins: () => apiOriginsFrom(settings) })
  } catch (error) {
    console.error('Design QA scenarios failed to install', error)
  }
}

/** The active scenario's feature flags, merged into the app's overrides but never saved with them. */
export function getDesignQaScenarioFlags(): Record<string, boolean | string> | undefined {
  return state.scenarioFlags
}

const ControlsDock = DESIGN_QA_SCENARIOS_ENABLED ? lazy(() => import('./ControlsDock')) : null

/** The bottom-right design QA dock; renders nothing when scenarios are off. */
export function DesignQaControls() {
  return ControlsDock ? (
    <Suspense fallback={null}>
      <ControlsDock />
    </Suspense>
  ) : null
}
